import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { blockText, parseNarrative } from "@hamlak/shared";
import { BottomCTA } from "@hamlak/ui";
import appIconUrl from "@hamlak/ui/brand/app-icon.png";
import catSymbolUrl from "@hamlak/ui/brand/cat-symbol.svg";
import type { PlotDetail } from "@/api/types";
import { usePlot } from "@/features/plot/usePlot";
import { t } from "@/i18n/ko";
import { useUiStore } from "@/stores/uiStore";
import { usePushStore } from "@/stores/pushStore";
import { enablePush } from "./push";
import "./push.css";

/** returnTo 가 채팅방·플롯이면 그 키(/chat/:key · /plots/:key) */
const plotKeyOf = (path: string | null) => (path ? (/^\/(?:chat|plots)\/([^/?#]+)/.exec(path)?.[1] ?? null) : null);

/** 첫 장면에서 캐릭터(main) 대사 블록의 첫 대사 문단(지문 제외) */
const firstLineOf = (plot: PlotDetail) => {
  for (const b of plot.firstScene) {
    if (b.type !== "dialogue" || b.speaker !== "main") continue;
    const line = parseNarrative(b.body).find((n) => n.kind === "dialogue");
    if (line) return blockText(line).replace(/\s+/g, " ").trim();
  }
  return "";
};

/** 미리보기: 복귀할 캐릭터가 있으면 그 이름·사진·첫 장면의 첫 대사, 없으면 시안 기본(철벽남 · 시안 대사 · 앱 심볼) */
function previewOf(plot: PlotDetail | undefined) {
  const text = plot ? firstLineOf(plot) : "";
  return {
    name: plot?.character.name ?? t("push.ask.defaultName"),
    avatarUrl: plot ? (plot.character.images[0] ?? plot.coverUrl) : null,
    line: `"${text || t("push.ask.defaultLine")}"`,
  };
}

/**
 * F01-15 알림 권한 안내(소프트 애스크, 시안 91:3284) → F01-16 OS 권한(시스템 창). 가입 직후 웰컴(F01-06) 다음, 앱에서 OS 권한이 아직 "묻지 않음"일 때 1회(ProfileNewPage).
 * "알림 받기" = OS 권한 요청 → 허용이면 토큰 등록. "나중에" = OS 창 없이 넘어감. 어느 쪽이든 returnTo ?? /home 으로(⑩ 대화 계속).
 */
export function PushPermissionPage() {
  const navigate = useNavigate();
  const [returnTo] = useState(() => useUiStore.getState().returnTo);
  const plot = usePlot(plotKeyOf(returnTo) ?? undefined);
  const [pending, setPending] = useState(false);
  const preview = previewOf(plot.data);

  // 화면이 떴으면 "물어봄" — 뒤로가기·앱 종료로 나가도 다시 띄우지 않는다
  useEffect(() => {
    usePushStore.getState().markAsked();
  }, []);

  const leave = () => {
    useUiStore.getState().setReturnTo(null);
    navigate(returnTo ?? "/home", { replace: true });
  };

  const accept = async () => {
    setPending(true);
    try {
      await enablePush();
    } finally {
      setPending(false);
      leave();
    }
  };

  return (
    <main className="page push-ask" data-testid="push-permission-page">
      <div className="push-ask__body">
        <div className="push-ask__preview" aria-hidden>
          <div className="push-ask__app">
            <img className="push-ask__appIcon" src={appIconUrl} alt="" />
            <span className="push-ask__appName">{t("push.ask.appName")}</span>
            <span className="push-ask__time">{t("push.ask.now")}</span>
          </div>
          <div className="push-ask__message">
            {preview.avatarUrl ? (
              <img className="push-ask__avatar" src={preview.avatarUrl} alt="" />
            ) : (
              <span className="push-ask__avatar push-ask__avatar--symbol">
                <img src={catSymbolUrl} alt="" />
              </span>
            )}
            <div className="push-ask__text">
              <span className="push-ask__name">{preview.name}</span>
              <span className="push-ask__line">{preview.line}</span>
            </div>
          </div>
        </div>
        <h1 className="push-ask__title">{t("push.ask.title")}</h1>
        <p className="push-ask__desc">{t("push.ask.body")}</p>
      </div>
      <BottomCTA
        primary={{ children: t("push.ask.cta"), variant: "brandSolid", loading: pending, onClick: () => void accept() }}
        secondary={{ children: t("push.ask.later"), disabled: pending, onClick: leave }}
      />
    </main>
  );
}
